import React, { useMemo } from 'react';
import { GasRangeCalendar, useGasI18n } from '../../../shared/kit';
import type { GasCalendarDayState } from '../../../shared/kit';
import type { RentalCalendar } from '../types';

/**
 * Adaptateur LOCATION du calendrier réutilisable `GasRangeCalendar` (kit).
 *
 * Toute la logique générique (grille, navigation, sélection de plage, légende)
 * vit dans le kit. Ici on ne fournit QUE la sémantique métier via getDayState :
 * occupation (libre/partiel/complet) calculée depuis les périodes occupées +
 * capacité, et jours fériés (grisés).
 */

const DAY_MS = 86400000;

function ymd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function AvailabilityCalendar({
    calendar,
    start,
    end,
    holidays = [],
    locale,
    onSelectRange,
}: {
    calendar: RentalCalendar;
    start: Date | null;
    end: Date | null;
    holidays?: string[];
    locale?: string;
    onSelectRange: (start: Date | null, end: Date | null) => void;
}) {
    const { t } = useGasI18n();
    const holidaySet = useMemo(() => new Set(holidays), [holidays]);
    const cap = Math.max(1, calendar.capacity);

    // Occupation par jour (clé ymd → quantité cumulée), pré-calculée sur l'horizon.
    const occByDay = useMemo(() => {
        const map: Record<string, number> = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        for (let i = 0; i < 400; i++) {
            const ds = new Date(today.getTime() + i * DAY_MS);
            const de = new Date(ds.getTime() + DAY_MS);
            let occ = 0;
            for (const b of calendar.busy) {
                const bs = new Date(b.start.replace(' ', 'T')).getTime();
                const be = new Date(b.end.replace(' ', 'T')).getTime();
                if (bs < de.getTime() && be > ds.getTime()) occ += b.quantity;
            }
            map[ymd(ds)] = occ;
        }
        return map;
    }, [calendar]);

    function getDayState(d: Date): GasCalendarDayState {
        const key = ymd(d);
        if (holidaySet.has(key)) {
            return { tone: 'holiday', disabled: true, title: t('rental.calendar.holiday') };
        }
        const occ = occByDay[key] ?? 0;
        const remaining = Math.max(0, cap - occ);
        const full = remaining <= 0;
        // Badge « restant » seulement si plusieurs exemplaires (sinon binaire).
        const badge = !full && cap > 1 ? remaining : undefined;
        return {
            tone: full ? 'full' : occ > 0 ? 'partial' : 'free',
            disabled: full,
            badge,
            title: full
                ? t('rental.calendar.full')
                : t('rental.calendar.remaining', { n: String(remaining), total: String(cap) }),
        };
    }

    return (
        <GasRangeCalendar
            start={start}
            end={end}
            onSelectRange={onSelectRange}
            getDayState={getDayState}
            locale={locale}
            legend={[
                { tone: 'free', label: t('rental.calendar.free') },
                { tone: 'partial', label: t('rental.calendar.partial') },
                { tone: 'full', label: t('rental.calendar.full') },
            ]}
            className="gas-rental-cal"
        />
    );
}
